import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './AuthContext';
import { Toaster } from 'sonner';
import './i18n';
import './App.css';

// Pages
import Login from './pages/Login';
import LenderDashboard from './pages/LenderDashboard';
import AdminDashboard from './pages/AdminDashboard';
import Loans from './pages/Loans';
import Contracts from './pages/Contracts';
import LoanDetail from './pages/LoanDetail';
import AdminLenders from './pages/AdminLenders';
import AdminLoans from './pages/AdminLoans';
import EditContract from './pages/EditContract';
import Profile from './pages/Profile';
import AdminLoanRequests from './pages/AdminLoanRequests';
import AdminRateConfig from './pages/AdminRateConfig';
import LandingPage from './pages/LandingPage';
import AdminWaitlist from './pages/AdminWaitlist';
import MyInvitations from './pages/MyInvitations';
import AdminCancellations from './pages/AdminCancellations';
import AdminApprovedCancellations from './pages/AdminApprovedCancellations';
import AdminLandingSettings from './pages/AdminLandingSettings';
import AdminCDNStatus from './pages/AdminCDNStatus';
import CompleteProfile from './pages/CompleteProfile';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/waitlist" element={<LandingPage />} />
      <Route path="/login" element={<Login />} />
      
      {/* Complete Profile - Protected but accessible before full profile */}
      <Route
        path="/complete-profile"
        element={
          <ProtectedRoute>
            <CompleteProfile />
          </ProtectedRoute>
        }
      />
      
      {/* Dashboard Route - redirects based on role */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {user?.role === 'admin' ? <AdminDashboard /> : <LenderDashboard />}
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/loans"
        element={
          <ProtectedRoute>
            <Loans />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/loans/:loanId"
        element={
          <ProtectedRoute>
            <LoanDetail />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/contracts"
        element={
          <ProtectedRoute>
            <Contracts />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/lenders"
        element={
          <ProtectedRoute adminOnly>
            <AdminLenders />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/loans"
        element={
          <ProtectedRoute adminOnly>
            <AdminLoans />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/contracts/edit/:loanId"
        element={
          <ProtectedRoute adminOnly>
            <EditContract />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/loan-requests"
        element={
          <ProtectedRoute adminOnly>
            <AdminLoanRequests />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/config/rate-tiers"
        element={
          <ProtectedRoute adminOnly>
            <AdminRateConfig />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/landing-settings"
        element={
          <ProtectedRoute adminOnly>
            <AdminLandingSettings />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/invitations"
        element={
          <ProtectedRoute>
            <MyInvitations />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/waitlist"
        element={
          <ProtectedRoute adminOnly>
            <AdminWaitlist />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/cancellations"
        element={
          <ProtectedRoute adminOnly>
            <AdminCancellations />
          </ProtectedRoute>
        }
      />
      
      <Route
        path="/admin/cancellations/approved"
        element={
          <ProtectedRoute adminOnly>
            <AdminApprovedCancellations />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/cdn-status"
        element={
          <ProtectedRoute adminOnly>
            <AdminCDNStatus />
          </ProtectedRoute>
        }
      />

      
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <AppRoutes />
      </Router>
      <Toaster position="top-right" richColors closeButton theme="dark" />
    </AuthProvider>
  );
}

export default App;